import { useState, useCallback, useRef } from 'react';
import {
  revalidateHotelAvailability,
  createHotelBooking,
  createNonRefundableInquiry,
} from '../api/hotels.api';
import { HotelBookingRejectedError, HotelBookingUnverifiedError } from '../api/hotelBookingErrors';
import { buildBookingRooms } from '../utils/buildBookingRooms';
import { isWithinBookingBlockWindow } from '../utils/bookingBlockWindow';
import {
  evaluateCancellationPolicy,
  type CancellationDisposition,
} from '../utils/cancellationPolicy';
import type { HotelDetailRevalidation } from '../types/hotels.presentation';
import type {
  BookingContact,
  BookingRoom,
  CancellationPolicy,
  HotelAvailabilityResponse,
  HotelAvailabilityRoom,
} from '../api/hotels.types';

export type HotelBookingStatus =
  | 'idle'
  | 'revalidating'
  | 'form'
  | 'submitting'
  | 'success'
  | 'error';

export type RatePolicyMode = CancellationDisposition;

export interface BookingFormData {
  contact: BookingContact;
  /** Passengers grouped by booked room (ids from the availability quote). */
  rooms: BookingRoom[];
  comments?: string;
}

/**
 * The rate combination the user picked: the offer token plus one rate key per entry of the
 * offer's `rooms[]` (several in multi-room searches). `key` identifies it in the UI.
 */
export interface BookingSelection {
  key: string;
  offerToken: string;
  rateKeys: string[];
}

export interface BookingContext {
  agencyEmail: string;
  hotelName: string;
  destination: string;
  checkin: string;
  checkout: string;
  /** Price shown for the selected option in the search results. */
  totalPrice?: number;
  currency?: string;
}

/** Price of the availability quote (every room, whole stay). */
export interface QuotedPrice {
  sale: number;
  currency?: string;
}

/**
 * Why the form was reopened instead of submitting: the quote expired and was refreshed, and the
 * new one changed the price or the cancellation conditions — the user must review before sending.
 */
export type QuoteRefreshNotice = 'priceChanged' | 'conditionsChanged' | null;

interface AvailabilityQuote {
  offerToken: string;
  rateKeys: string[];
  availabilityToken: string;
  disposition: CancellationDisposition;
  rooms: HotelAvailabilityRoom[];
  cancellationPolicy: CancellationPolicy[];
  price: QuotedPrice | null;
  quotedAt: number;
}

/**
 * How long an availability quote (and its token) is trusted. Older quotes are refreshed before
 * opening the form and before submitting, so a booking never goes out with a stale token.
 * Same 5 minutes as the stored hotel detail.
 */
export const QUOTE_TTL_MS = 5 * 60 * 1000;

/** Search and availability prices may differ by cents from rounding: not a price change. */
function isSamePrice(a: number, b: number): boolean {
  return Math.abs(a - b) <= Math.max(0.05, Math.abs(b) * 0.005);
}

function readCancellationPolicy(result: HotelAvailabilityResponse): CancellationPolicy[] {
  return result.policies?.cancellation ?? result.hotel?.policies?.cancellation ?? [];
}

/** Total of the quote: `hotel.price.sale`, or the sum of its rooms when it is missing. */
function readQuotedPrice(result: HotelAvailabilityResponse): QuotedPrice | null {
  const currency = result.hotel?.currency;
  const total = result.hotel?.price?.sale;
  if (typeof total === 'number' && Number.isFinite(total) && total > 0) {
    return { sale: total, currency };
  }

  const roomPrices = (result.hotel?.rooms ?? []).map((room) => room.rate?.price?.sale);
  if (roomPrices.length === 0) return null;
  if (!roomPrices.every((sale) => typeof sale === 'number' && Number.isFinite(sale))) return null;

  return { sale: roomPrices.reduce((sum, sale) => sum + sale, 0), currency };
}

function toQuote(
  { offerToken, rateKeys }: Pick<HotelDetailRevalidation, 'offerToken' | 'rateKeys'>,
  result: HotelAvailabilityResponse,
): AvailabilityQuote {
  const cancellationPolicy = readCancellationPolicy(result);
  return {
    offerToken,
    rateKeys,
    availabilityToken: result.availabilityToken,
    disposition: evaluateCancellationPolicy(cancellationPolicy).disposition,
    rooms: result.hotel?.rooms ?? [],
    cancellationPolicy,
    price: readQuotedPrice(result),
    quotedAt: Date.now(),
  };
}

function sameRateKeys(a: string[], b: string[]): boolean {
  return a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');
}

function isFresh(quote: AvailabilityQuote): boolean {
  return Date.now() - quote.quotedAt < QUOTE_TTL_MS;
}

export interface UseHotelBookingReturn {
  status: HotelBookingStatus;
  bookLocator: string | null;
  /**
   * How the provider left a successful booking: confirmed right away, or pending its
   * confirmation (BM IP/CH/RQ) — the user must not be told it is confirmed. `null` for inquiries.
   */
  bookingOutcome: 'confirmed' | 'pending' | null;
  /**
   * False when the failed booking may still have been made (no answer from the provider):
   * offering "try again" could book twice.
   */
  canRetryBooking: boolean;
  errorMessage: string | null;
  selectedRateKey: string | null;
  availabilityToken: string | null;
  isNonRefundable: boolean;
  /** Rooms of the current availability quote (one per physical room, with occupancy). */
  availabilityRooms: HotelAvailabilityRoom[];
  /** Cancellation schedule of the current quote, shown to the user before confirming. */
  cancellationPolicy: CancellationPolicy[];
  /** Price confirmed by the current quote (null when the provider does not send it). */
  quotedPrice: QuotedPrice | null;
  /** Price the user saw in the results for the selected option. */
  searchPrice: number | null;
  quoteRefreshNotice: QuoteRefreshNotice;
  /** Data typed in the form before an error or refresh, to fill it again. */
  formDraft: BookingFormData | null;
  initiateBooking: (
    selection: BookingSelection,
    revalidation: HotelDetailRevalidation,
    context: BookingContext,
  ) => Promise<void>;
  /** Cancellation disposition per option (`BookingSelection.key`) from its availability quote. */
  ratePolicyModes: Record<string, RatePolicyMode>;
  /** True once the per-option availability checks of the detail finished. */
  policyQuotesReady: boolean;
  /**
   * Quotes every option ahead of time (on detail entry) and caches the quotes.
   * @returns false when every quote failed.
   */
  prefetchAvailability: (searchToken: string, selections: BookingSelection[]) => Promise<boolean>;
  submitBooking: (data: BookingFormData) => Promise<void>;
  cancelBooking: () => void;
  retryBooking: () => void;
}

export function useHotelBooking(): UseHotelBookingReturn {
  const [status, setStatus] = useState<HotelBookingStatus>('idle');
  const [bookLocator, setBookLocator] = useState<string | null>(null);
  const [bookingOutcome, setBookingOutcome] = useState<'confirmed' | 'pending' | null>(null);
  const [canRetryBooking, setCanRetryBooking] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selection, setSelection] = useState<BookingSelection | null>(null);
  const [savedRevalidation, setSavedRevalidation] = useState<HotelDetailRevalidation | null>(null);
  const [savedContext, setSavedContext] = useState<BookingContext | null>(null);
  const [quote, setQuote] = useState<AvailabilityQuote | null>(null);
  const [prefetchedQuotes, setPrefetchedQuotes] = useState<Record<string, AvailabilityQuote>>({});
  const [policyQuotesReady, setPolicyQuotesReady] = useState(false);
  const [quoteRefreshNotice, setQuoteRefreshNotice] = useState<QuoteRefreshNotice>(null);
  const [formDraft, setFormDraft] = useState<BookingFormData | null>(null);
  // Synchronous guard: a double click fires twice before React re-renders the disabled button.
  const submittingRef = useRef(false);

  const requiresInquiry = useCallback(
    (candidate: AvailabilityQuote, context: BookingContext) =>
      candidate.disposition !== 'bookable' || isWithinBookingBlockWindow(context.checkin),
    [],
  );

  const cacheQuote = useCallback((key: string, cached: AvailabilityQuote) => {
    setPrefetchedQuotes((prev) => ({ ...prev, [key]: cached }));
  }, []);

  const prefetchAvailability = useCallback(
    async (searchToken: string, selections: BookingSelection[]) => {
      setPolicyQuotesReady(false);

      const settled = await Promise.all(
        selections.map(async (target) => {
          try {
            const result = await revalidateHotelAvailability({
              searchToken,
              token: target.offerToken,
              rates: target.rateKeys.map((key) => ({ key })),
            });
            return { key: target.key, quote: toQuote(target, result) };
          } catch {
            return { key: target.key, quote: null };
          }
        }),
      );

      setPrefetchedQuotes((prev) => {
        const next = { ...prev };
        for (const { key, quote: settledQuote } of settled) {
          if (settledQuote) next[key] = settledQuote;
          else delete next[key];
        }
        return next;
      });
      setPolicyQuotesReady(true);

      return selections.length === 0 || settled.some(({ quote: settledQuote }) => settledQuote);
    },
    [],
  );

  const fetchQuote = useCallback(
    async (target: BookingSelection, revalidation: HotelDetailRevalidation) =>
      toQuote(
        target,
        await revalidateHotelAvailability({
          searchToken: revalidation.searchToken,
          token: target.offerToken,
          rates: target.rateKeys.map((key) => ({ key })),
        }),
      ),
    [],
  );

  const openForm = useCallback(
    async (
      nextSelection: BookingSelection,
      revalidation: HotelDetailRevalidation,
      context: BookingContext,
      { reuseCachedQuote }: { reuseCachedQuote: boolean },
    ) => {
      setSelection(nextSelection);
      setSavedRevalidation(revalidation);
      setSavedContext(context);
      setErrorMessage(null);
      setQuoteRefreshNotice(null);

      const prefetched = prefetchedQuotes[nextSelection.key];
      const cachedQuote =
        reuseCachedQuote &&
        prefetched &&
        isFresh(prefetched) &&
        prefetched.offerToken === nextSelection.offerToken &&
        sameRateKeys(prefetched.rateKeys, nextSelection.rateKeys)
          ? prefetched
          : null;

      if (cachedQuote) {
        setQuote(cachedQuote);
        setStatus('form');
        return;
      }

      setStatus('revalidating');

      try {
        const fetched = await fetchQuote(nextSelection, revalidation);
        cacheQuote(nextSelection.key, fetched);
        setQuote(fetched);
        setStatus('form');
      } catch {
        setErrorMessage('hotels.booking.error.availabilityFailed');
        setStatus('error');
      }
    },
    [prefetchedQuotes, fetchQuote, cacheQuote],
  );

  const initiateBooking = useCallback(
    (
      nextSelection: BookingSelection,
      revalidation: HotelDetailRevalidation,
      context: BookingContext,
    ) => {
      setFormDraft(null);
      return openForm(nextSelection, revalidation, context, { reuseCachedQuote: true });
    },
    [openForm],
  );

  const submitBooking = useCallback(
    async (data: BookingFormData) => {
      if (submittingRef.current) return;

      if (!quote?.availabilityToken || !savedContext) {
        setErrorMessage('hotels.booking.error.noToken');
        setStatus('error');
        return;
      }

      submittingRef.current = true;
      setStatus('submitting');
      setErrorMessage(null);
      setFormDraft(data);

      try {
        let current = quote;

        // The token may have expired while the user typed: get a fresh one before sending.
        if (!isFresh(current) && selection && savedRevalidation) {
          try {
            const refreshed = await fetchQuote(selection, savedRevalidation);
            const conditionsChanged =
              requiresInquiry(refreshed, savedContext) !== requiresInquiry(current, savedContext);
            const priceChanged =
              !!refreshed.price &&
              !!current.price &&
              !isSamePrice(refreshed.price.sale, current.price.sale);
            setQuote(refreshed);
            cacheQuote(selection.key, refreshed);
            if (conditionsChanged || priceChanged) {
              setQuoteRefreshNotice(conditionsChanged ? 'conditionsChanged' : 'priceChanged');
              setStatus('form');
              return;
            }
            current = refreshed;
          } catch {
            setErrorMessage('hotels.booking.error.availabilityFailed');
            setStatus('error');
            return;
          }
        }

        const rooms = buildBookingRooms(data.rooms, current.rooms);

        if (requiresInquiry(current, savedContext)) {
          await createNonRefundableInquiry({
            agencyEmail: savedContext.agencyEmail,
            availabilityToken: current.availabilityToken,
            hotelName: savedContext.hotelName,
            destination: savedContext.destination,
            checkin: savedContext.checkin,
            checkout: savedContext.checkout,
            contact: data.contact,
            rooms,
            comments: data.comments || undefined,
          });
        } else {
          const result = await createHotelBooking({
            availabilityToken: current.availabilityToken,
            contact: data.contact,
            rooms,
            comments: data.comments || undefined,
            agencyEmail: savedContext.agencyEmail || undefined,
            feature: { confirmation: false },
            hotelName: savedContext.hotelName,
            destination: savedContext.destination,
            checkin: savedContext.checkin,
            checkout: savedContext.checkout,
            cancellationPolicy: current.cancellationPolicy,
            // The quote's price is the one being booked; the search price is only a fallback.
            totalPrice: current.price?.sale ?? savedContext.totalPrice,
            currency: current.price?.currency ?? savedContext.currency,
          });
          // A rejected status that reached us as a body (older backend) is still a failed booking.
          if (result.outcome === 'rejected') throw new HotelBookingRejectedError();
          setBookLocator(String(result.locator));
          setBookingOutcome(result.outcome === 'confirmed' ? 'confirmed' : 'pending');
        }

        setFormDraft(null);
        setStatus('success');
      } catch (error) {
        const unverified = error instanceof HotelBookingUnverifiedError;
        setCanRetryBooking(!unverified);
        setErrorMessage(
          unverified
            ? 'hotels.booking.error.bookingUnverified'
            : error instanceof HotelBookingRejectedError
              ? 'hotels.booking.error.bookingRejected'
              : 'hotels.booking.error.bookingFailed',
        );
        setStatus('error');
      } finally {
        submittingRef.current = false;
      }
    },
    [quote, savedContext, selection, savedRevalidation, fetchQuote, requiresInquiry, cacheQuote],
  );

  const cancelBooking = useCallback(() => {
    // Closing while a request is in flight would hide its outcome from the user.
    if (submittingRef.current) return;
    setStatus('idle');
    setErrorMessage(null);
    setSelection(null);
    setSavedRevalidation(null);
    setSavedContext(null);
    setQuote(null);
    setBookLocator(null);
    setBookingOutcome(null);
    setCanRetryBooking(true);
    setQuoteRefreshNotice(null);
    setFormDraft(null);
  }, []);

  const retryBooking = useCallback(() => {
    if (selection && savedRevalidation && savedContext) {
      // Always a fresh quote: the previous token may be the reason it failed (and is single-use).
      void openForm(selection, savedRevalidation, savedContext, { reuseCachedQuote: false });
    } else {
      cancelBooking();
    }
  }, [selection, savedRevalidation, savedContext, openForm, cancelBooking]);

  const ratePolicyModes: Record<string, RatePolicyMode> = {};
  for (const [key, cached] of Object.entries(prefetchedQuotes)) {
    ratePolicyModes[key] = cached.disposition;
  }

  return {
    status,
    bookLocator,
    bookingOutcome,
    canRetryBooking,
    errorMessage,
    selectedRateKey: selection?.key ?? null,
    availabilityToken: quote?.availabilityToken ?? null,
    isNonRefundable: !!quote && !!savedContext && requiresInquiry(quote, savedContext),
    availabilityRooms: quote?.rooms ?? [],
    cancellationPolicy: quote?.cancellationPolicy ?? [],
    quotedPrice: quote?.price ?? null,
    searchPrice: savedContext?.totalPrice ?? null,
    quoteRefreshNotice,
    formDraft,
    ratePolicyModes,
    policyQuotesReady,
    initiateBooking,
    prefetchAvailability,
    submitBooking,
    cancelBooking,
    retryBooking,
  };
}
